import { useMutation } from "@tanstack/react-query";
import { useApiQuery, apiRequest } from "@/shared/api";
import type { ApiError } from "@/shared/api";
import type { UseQueryOptions } from "@tanstack/react-query";

// ==================== Types ====================

export interface ReferralCodeResponse {
  referralCode: string;
}

export interface ValidateReferralCodePayload {
  referralCode: string;
}

export interface ValidateReferralCodeResponse {
  valid: boolean;
  referrer?: {
    id: string;
    name: string;
    referralCode: string;
  };
}

export interface ReferralUser {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  kycCompleted: boolean;
}

export interface ReferralStatsResponse {
  referralCode: string;
  totalReferrals: number;
  totalEarnings: number;
  referrals: ReferralUser[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export interface PendingReward {
  rewardType: string;
  amount: number;
  currency: string;
  triggerEvent: string;
  createdAt: string;
}

export interface PendingRewardsResponse {
  pendingRewards: PendingReward[];
  totalPending: number;
}

export interface RewardHistoryItem {
  id: string;
  rewardType: string;
  amount: number;
  currency: string;
  status: string;
  triggerEvent: string;
  createdAt: string;
  creditedAt?: string;
  transactionReference?: string;
}

export interface RewardHistoryResponse {
  rewards: RewardHistoryItem[];
  totalCredited: number;
  totalPending: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export interface ReferralStatsParams {
  limit?: number;
  offset?: number;
}

export interface RewardHistoryParams {
  limit?: number;
  offset?: number;
}

// ==================== API Functions ====================

/**
 * Get user's referral code
 */
export const getReferralCode = async () =>
  apiRequest<ReferralCodeResponse>({
    url: "/referral/code",
    method: "get",
  });

/**
 * Validate a referral code
 */
const isValidateReferralCodeResponse = (
  payload: unknown
): payload is ValidateReferralCodeResponse =>
  typeof payload === "object" &&
  payload !== null &&
  typeof (payload as { valid?: unknown }).valid === "boolean";

export const normalizeValidateReferralCodeResponse = (
  payload: unknown
): ValidateReferralCodeResponse => {
  if (isValidateReferralCodeResponse(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object") {
    const nested = (payload as { data?: unknown }).data;
    if (isValidateReferralCodeResponse(nested)) {
      return nested;
    }

    if (nested && typeof nested === "object") {
      const deeper = (nested as { data?: unknown }).data;
      if (isValidateReferralCodeResponse(deeper)) {
        return deeper;
      }
    }
  }

  return { valid: false };
};

export const validateReferralCode = async (
  payload: ValidateReferralCodePayload
): Promise<ValidateReferralCodeResponse> => {
  const response = await apiRequest<unknown>({
    url: "/referral/validate-code",
    method: "post",
    data: payload,
  });
  return normalizeValidateReferralCodeResponse(response);
};

/**
 * Get referral statistics
 */
export const getReferralStats = async (params: ReferralStatsParams = {}) => {
  const { limit = 10, offset = 0 } = params;
  return apiRequest<ReferralStatsResponse>({
    url: `/referral/stats?limit=${limit}&offset=${offset}`,
    method: "get",
  });
};

/**
 * Get pending rewards
 */
export const getPendingRewards = async () =>
  apiRequest<PendingRewardsResponse>({
    url: "/referral/pending-rewards",
    method: "get",
  });

/**
 * Get reward history
 */
export const getRewardHistory = async (params: RewardHistoryParams = {}) => {
  const { limit = 20, offset = 0 } = params;
  return apiRequest<RewardHistoryResponse>({
    url: `/referral/history?limit=${limit}&offset=${offset}`,
    method: "get",
  });
};

// ==================== React Query Hooks ====================

/**
 * Hook to get user's referral code
 */
export const useReferralCode = (
  options?: Omit<
    UseQueryOptions<ReferralCodeResponse, ApiError>,
    "queryKey" | "queryFn"
  >
) =>
  useApiQuery<ReferralCodeResponse, ApiError>(
    ["referral", "code"],
    {
      url: "/referral/code",
      method: "get",
    },
    options as never
  );

/**
 * Hook to validate referral code
 */
export const useValidateReferralCode = (options?: {
  onSuccess?: (
    data: ValidateReferralCodeResponse,
    variables: ValidateReferralCodePayload,
    context: unknown
  ) => void;
  onError?: (
    error: ApiError,
    variables: ValidateReferralCodePayload,
    context: unknown
  ) => void;
  onSettled?: (
    data: ValidateReferralCodeResponse | undefined,
    error: ApiError | null,
    variables: ValidateReferralCodePayload,
    context: unknown
  ) => void;
}) =>
  useMutation<
    ValidateReferralCodeResponse,
    ApiError,
    ValidateReferralCodePayload
  >({
    mutationFn: validateReferralCode,
    ...options,
  });

/**
 * Hook to get referral statistics
 */
export const useReferralStats = (
  params: ReferralStatsParams = {},
  options?: Omit<
    UseQueryOptions<ReferralStatsResponse, ApiError>,
    "queryKey" | "queryFn"
  >
) => {
  const { limit = 10, offset = 0 } = params;
  return useApiQuery<ReferralStatsResponse, ApiError>(
    ["referral", "stats", limit, offset],
    {
      url: `/referral/stats?limit=${limit}&offset=${offset}`,
      method: "get",
    },
    options as never
  );
};

/**
 * Hook to get pending rewards
 */
export const usePendingRewards = (
  options?: Omit<
    UseQueryOptions<PendingRewardsResponse, ApiError>,
    "queryKey" | "queryFn"
  >
) =>
  useApiQuery<PendingRewardsResponse, ApiError>(
    ["referral", "pending-rewards"],
    {
      url: "/referral/pending-rewards",
      method: "get",
    },
    options as never
  );

/**
 * Hook to get reward history
 */
export const useRewardHistory = (
  params: RewardHistoryParams = {},
  options?: Omit<
    UseQueryOptions<RewardHistoryResponse, ApiError>,
    "queryKey" | "queryFn"
  >
) => {
  const { limit = 20, offset = 0 } = params;
  return useApiQuery<RewardHistoryResponse, ApiError>(
    ["referral", "history", limit, offset],
    {
      url: `/referral/history?limit=${limit}&offset=${offset}`,
      method: "get",
    },
    options as never
  );
};
